import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CmsAPI from '../../../common/apis/CmsAPI';
import { useUserContext } from '../../context/UserContext';
import { CustomButton } from '../Button/Button';
import CloseButton from '../CloseButton/CloseButton';
import { CustomInput } from '../Input/Input';
import Modal from '../Modal';
import NarrativeSettings from '../NarrativeSettings/NarrativeSettings';
import SelectClipsList from '../SelectClipsList/SelectClipsList';
import styles from './InviteModalContent.module.scss';
import cx from 'classnames';
import RadioButton from '../RadioButton/RadioButton';
import CollapsableContainer from '../CollapsableContainer/CollapsableContainer';

const INVITE_ROLES = {

  RECORDER: {
    type: 'RECORDER',
    title: 'storyboard.invite_modal.roles.recorded',
    description: 'storyboard.invite_modal.roles.recorded.description',
    permissions: [],
  },
  REVIEWER: {
    type: 'REVIEWER',
    title: 'company.settings.users.reviewer',
    description: 'storyboard.invite_modal.roles.reviewer.description',
    permissions: ['read'],
  },
  EDITOR: {
    type: 'EDITOR',
    title: 'folder.role.editor',
    description: 'storyboard.invite_modal.roles.editor.description',
    permissions: ['read', 'update'],
  },
}

const InviteModalContent = ({ id, onClose, onSubmit, scenes, selectedScene, videoAuthor, assignments, permissions, }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [seeStoryLine, setSeeStoryLine] = useState(false);
  const [canEditClips, setCanEditClips] = useState(false);
  const [isEmailFocus, setIsEmailFocus] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isUserExist, setIsUserExist] = useState(false);
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [showEmailWarning, setShowEmailWarning] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [chosenClips, setChosenClips] = useState([]);
  const [isInvitationLoading, setIsInvitationLoading] = useState(false);
  const [role, setRole] = useState(INVITE_ROLES.RECORDER.type);
  const isRecordedRole = useMemo(() => role === INVITE_ROLES.RECORDER.type, [role]);

  const filteredAssigned = useMemo(() => {
    // Show only unique users emails
    const assignees = assignments.map(item => item.assigned);
    const res = [...assignees.reduce((a, b) => {
      a.set(b.email, b);
      return a;
    }, new Map()).values()];
    return res;
  }, [email, assignments]);

  const { user, isInviteCreateUser } = useUserContext();

  const { t } = useTranslation();

  const isOwnerEmail = useMemo(() => user.userEmail === email.toLowerCase(), [email, user]);

  const isInviteBtnEnabled = useMemo(() => {
    const userCheck = isInviteCreateUser ? isEmailValid : isUserExist || organization !== null;
    const roleCheck = role !== INVITE_ROLES.RECORDER.type || chosenClips.length > 0;
    return userCheck && roleCheck;
  }, [isInviteCreateUser, isEmailValid, isUserExist, organization, chosenClips, seeStoryLine, role]);

  const handleSaveInvite = useCallback(async () => {
    let readPermission = false;
    let updatePermission = false;
    switch (role) {
      case INVITE_ROLES.EDITOR.type:
        readPermission = true;
        updatePermission = true;
        break;
      case INVITE_ROLES.REVIEWER.type:
        readPermission = true;
        break;
      default:
        readPermission = seeStoryLine;
        updatePermission = canEditClips;
    }
    try {
      setIsInvitationLoading(true);
      await onSubmit(chosenClips, name, email, message, readPermission, updatePermission);
    } catch (err) {
      console.log(err);
    }

    setIsInvitationLoading(false);
    onClose();
  }, [chosenClips, name, email, message, seeStoryLine, canEditClips, role]);

  const handleNameChange = e => {
    setName(e.target.value);
  };

  const handleChangeMessage = e => {
    setMessage(e.target.value);
  };

  const onBlurInput = () => {
    // make a small delay so that the user has time to click
    // on the dropdown before the dropdown closes
    setTimeout(() => setIsEmailFocus(false), 150);
  };

  const onFocusInput = () => {
    setIsEmailFocus(true);
  };

  const checkEmailFormat = email => {
    const re = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/gm;
    return re.test(email.toLowerCase());
  };

  const checkEmail = item => {
    const isEmailOwner = user?.email === item;
    if (isEmailOwner) {
      return false;
    }
    return true;
  };

  const getUserInfoByEmail = useCallback((email) => {
    if (!filteredAssigned.find(item => item.email === email)) {
      return null;
    }
    const usersPermissions = permissions.find(item => item.email === email);
    const clips = assignments.filter(item => item.assigned.email === email)?.filter(item => item.sceneId !== null)?.map(item => item.sceneId) ?? [];
    const readAction = usersPermissions?.storyPermissions?.find(({ action }) => action === 'read') !== undefined ?? false;
    const updateAction = usersPermissions?.storyPermissions?.find(({ action }) => action === 'update') !== undefined ?? false;
    const role = clips?.length === 0 ? updateAction ? INVITE_ROLES.EDITOR.type : INVITE_ROLES.REVIEWER.type : INVITE_ROLES.RECORDER.type;
    return { clips, readAction, updateAction, role };
  }, [assignments, permissions, filteredAssigned]);

  // change email in input field and check for validity
  const handleChangeEmail = event => {
    const changeInput = event.target.value.trim();
    setEmail(changeInput);
    setIsUserExist(false);
    if (checkEmailFormat(changeInput) && checkEmail(changeInput)) {
      setIsEmailValid(true);
      return;
    }
    setIsEmailValid(false);
    organization && setOrganization(null);
  };

  const selectUser = (e, email) => {
    e.stopPropagation();
    console.log('email', email);
    setEmail(email);
    setIsEmailValid(true);
  };

  const checkUserOrganization = useCallback(async () => {
    let userOrganization = organization;
    setIsUserExist(false);
    setIsUserLoading(true);

    if (!organization) {
      const userConfig = await CmsAPI.getAwsConfigForUsername(user.userEmail);
      userOrganization = userConfig[0].companyName;
      setOrganization(userConfig[0].companyName);
    }
    try {
      const assignedUserInfo = await CmsAPI.getAwsConfigForUsername(email);
      if (assignedUserInfo) {
        if (userOrganization !== assignedUserInfo[0].companyName) {
          setShowEmailWarning(true);
        }
        setIsUserExist(true);
      }
    } catch (e) {
      setShowEmailWarning(true);
    }

    setIsUserLoading(false);
  }, [organization, email, user, assignments, permissions]);

  const clearEmailField = () => {
    setEmail('');
    setIsEmailValid(false);
  };

  const handleCloseEmailWarning = () => {
    setShowEmailWarning(false);
    clearEmailField();
  };


  const handleConfirmEmailWarning = () => {
    setShowEmailWarning(false);
    setIsEmailValid(true);
  };

  const handleChooseClip = clip => {
    const belongsToAnotherUser = scenes.find(item => item.id === clip.id).author;
    if (belongsToAnotherUser?.username !== videoAuthor.username) {
      const confirmSwitch = confirm(
        t('storyboard.invite_modal.change_clip_owner_confirm', { name: scenes.find(item => item.id === clip.id).author.name })
      );
      if (!confirmSwitch) {
        return;
      }
    }
    setChosenClips(prevState => {
      if (prevState.length > 0 && prevState.find(item => item === clip.id)) {
        return prevState.filter(item => item !== clip.id);
      } else {
        return [...prevState, clip.id];
      }
    });
  };

  const handleCanReadToggle = useCallback(
    value => {
      if (!value && canEditClips) {
        setCanEditClips(false);
      }
      setSeeStoryLine(value);
    },
    [canEditClips]
  );

  const handleCanUpdateToggle = useCallback(
    value => {
      if (value && !seeStoryLine) {
        setSeeStoryLine(true);
      }
      setCanEditClips(value);
    },
    [seeStoryLine]
  );

  useEffect(() => {
    const clipId = !!id && scenes.find(item => item.id === id)?.id;
    if (clipId) {
      setChosenClips([clipId]);
    } else {
      setChosenClips([]);
    }
  }, [id, selectedScene]);

  useEffect(() => {
    if (!isEmailFocus && isEmailValid) {
      if (isOwnerEmail) {
        clearEmailField();
      }
      // Check if user is already invited to the project
      const userData = getUserInfoByEmail(email);
      if (userData) {
        const { clips, readAction, updateAction, role } = userData;
        setChosenClips(clips);
        setSeeStoryLine(readAction);
        setCanEditClips(updateAction);
        setRole(role);
        setIsUserExist(true);
      } else {
        if (!isInviteCreateUser) {
          checkUserOrganization();
        }
      }
    }
  }, [isEmailFocus]);


  return (
    <>
      <div className={styles.headerContainer}>
        <h1 className={styles.header}>{t('storyboard.editor.invite_modal.header')}</h1>
        <CloseButton onClose={onClose} />
      </div>
      <div className={styles.infoContainer}>
        {/* <CustomInput
          placeholder={t('storyboard.editor.invite_modal.name_field')}
          onChange={handleNameChange}
          value={name}
          type="text"
        /> */}
        <div className={styles.inputContainer}>
          <CustomInput
            placeholder={t('general.email')}
            onChange={handleChangeEmail}
            value={email}
            type="email"
            onFocusChanged={onBlurInput}
            onFocus={onFocusInput}
          />
          {isEmailValid && isEmailFocus && (
            <div className={cx(styles.inputDropdown, { [styles.error]: isOwnerEmail })}>
              <div className={styles.usersListItem}>
                {isOwnerEmail ? (
                  <span className={styles.ownerEmailError}>{t('storyboard.invite_modal.owner_invite_error')}</span>
                ) : (
                  <>
                    <div className={styles.photoPlaceholder}>{email[0]?.toUpperCase()}</div>
                    <span className={styles.email}>{email}</span>
                  </>
                )}
              </div>
            </div>
          )}
          {filteredAssigned.length && isEmailFocus && !isEmailValid ? <ul className={cx(styles.inputDropdown)}>
            {filteredAssigned.map(({ email, avatar, name, username }) => <li className={styles.usersListItem} key={username} onClick={(e) => selectUser(e, email)}>
              {name || avatar ? <div className={styles.photoPlaceholder}>{avatar ? <img src={avatar} className={styles.avatar} /> : name[0]?.toUpperCase()}</div> : null}
              <span>{name}</span>
              <span className={styles.email}>({email})</span>
            </li>)}
          </ul> : null}
        </div>
        <textarea
          value={message}
          onChange={handleChangeMessage}
          placeholder={t('storyboard.editor.invite_modal.message_field')}
          type="text"
          className={styles.textarea}
        />
      </div>
      <div className={styles.optionsList}>
        <h3 className={styles.header}>{t('storyboard.invite_modal.roles.title')}</h3>
        {Object.values(INVITE_ROLES).map(({ type, title, description }) => (
          <div key={type} className={cx(styles.roleContainer, { [styles.active]: role === type })}>
            <RadioButton className={styles.radioButton} type='radiobutton' checked={role === type} onChange={() => setRole(type)} disabled={type !== INVITE_ROLES.RECORDER.type && email === videoAuthor.email} />
            <div><p className={styles.roleName}>{t(title)}</p> <p className={styles.roleDescription}>{t(description)}</p></div>
          </div>
        ))}
      </div>
      <CollapsableContainer open={isRecordedRole}>

        <NarrativeSettings
          seeStoryLine={seeStoryLine}
          canEditClips={canEditClips}
          handleStoryLine={handleCanReadToggle}
          handleEditClips={handleCanUpdateToggle}
        />
        <SelectClipsList
          clips={scenes} selectedClips={chosenClips} handleChooseClip={handleChooseClip} />
      </CollapsableContainer>

      <CustomButton
        label={t('storyboard.editor.invite')}
        onClick={handleSaveInvite}
        variant="primary"
        className={styles.submitBtn}
        disabled={!isInviteBtnEnabled}
        loadingSpinner={true}
        loading={isUserLoading || isInvitationLoading}
      />

      <Modal className={styles.warningWrapper} open={showEmailWarning} onClose={handleCloseEmailWarning}>
        <p className={styles.message}

          dangerouslySetInnerHTML={{ __html: t('storyboard.invite_modal.other_company_warning', { email, organization }).replace(email, `<b>${email}</b>`).replace(organization, `<b>${organization}</b>`) }}
        />
        <div className={styles.buttonsContainer}>
          <CustomButton label={t('general.cancel')} variant="secondary" onClick={handleCloseEmailWarning} />
          <CustomButton label={t('storyboard.invite_modal.other_company_warning.confirm')} onClick={handleConfirmEmailWarning} variant="primary" />
        </div>
      </Modal>
    </>
  );
};

export default InviteModalContent;
